import { UploadIcon } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { useTranslate } from 'sbx-crm-menu-ui';
import RenderFormFieldsComponent, {
    GroupTypes,
} from 'src/components/shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/card';
import { AppAgentContext } from 'src/context/ContextProvider';
import { appActions } from 'src/store';
import { AiAgentV3, Flow, Retriever } from 'src/types/AiAgentV3';
import { ModalType } from 'src/types/Modal';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { grantPermission } from 'src/utils';
import { AgentSetupContextV3 } from '../AgentSetupV3Component';

const classNameLabelAgent = '!text-purple-700 !font-semibold';
const classNameInput =
    '!border-purple-300 !focus:border-purple-500 !focus:ring !focus:ring-purple-200 !focus:ring-opacity-50';

export const AgentFlowComponent = () => {
    const {
        state: {
            session: { permissions },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { control, flows, watch, getValues } = useContext(AgentSetupContextV3);
    const { t: c } = useTranslate('common');
    const { t } = useTranslate('agent');

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const flowOptions = useMemo(() => {
        return (flows as Flow[])?.map((flow) => ({
            label: flow.agent.replaceAll('_', ' '),
            value: flow.agent,
        }));
    }, [flows]);

    const agentFields = useMemo((): GroupTypes<keyof AiAgentV3> => {
        return {
            agent1: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'main_model',
                        label: t('flow'),
                        type: 'select',
                        classNameLabel: classNameLabelAgent,
                        classNameInput,
                        placeholder: t('select_flow'),
                        disabled: !allowEdit,
                        options: flowOptions,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
        };
    }, [flowOptions]);

    const retrievers = useMemo(() => {
        const flow = flows?.find((flow) => flow.agent === watch('main_model'));
        return flow?.retrievers;
    }, [watch('main_model'), flows]);

    const openDocumentsModal = (retriever: Retriever) => {
        dispatch(
            appActions.openModal({
                type: ModalType.DOCUMENT_RETRIEVERS,
                data: {
                    title: c('documents'),
                    onClose: () => dispatch(appActions.closeModal(ModalType.DOCUMENT_RETRIEVERS)),
                    retriever,
                    agent: getValues('main_model') || '',
                },
            })
        );
    };

    return (
        <Card className="bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
            <CardHeader>
                <CardTitle className="text-2xl font-bold text-purple-800">{t('flow_settings')}</CardTitle>
            </CardHeader>

            <CardContent>
                <div className="grid grid-cols-1 gap-6 mb-5">
                    {Object.keys(agentFields).map((group) => {
                        const currentGroup = agentFields[group];

                        return (
                            <div className={`flex flex-col`} key={group}>
                                {currentGroup.label && currentGroup.label}
                                <div className={`grid ${currentGroup.columns ?? 'grid-cols-1 '} gap-3 mb-3`}>
                                    <RenderFormFieldsComponent fields={currentGroup.items} control={control} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                {Object.values(retrievers || {}).length > 0 && (
                    <div>
                        <h3 className="block text-sm  leading-6 !text-purple-700 !font-semibold">{t('retrievers')}</h3>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {Object.values(retrievers || {}).map((retriever, index) => (
                                <Card key={index} className="relative shadow rounded-lg">
                                    <CardHeader>
                                        <div className="flex items-start justify-between">
                                            <div className="space-y-1">
                                                <CardTitle className="text-lg capitalize">
                                                    {retriever.name?.split(/_/).join(' ')}
                                                </CardTitle>
                                            </div>
                                            {retriever.folder && (
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        title={c('upload_files')}
                                                        onClick={() => openDocumentsModal(retriever)}
                                                        className="h-8 w-8 p-0"
                                                    >
                                                        <UploadIcon className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="secondary" className="text-xs">
                                                {retriever.name}
                                            </Badge>
                                            {retriever.type && (
                                                <Badge variant="outline" className="text-xs">
                                                    {retriever.type}
                                                </Badge>
                                            )}
                                        </div>
                                    </CardHeader>
                                    {retriever.description && (
                                        <CardContent className="space-y-4">
                                            <div>
                                                <h3 className="font-bold text-[14px]">{c('description')}</h3>
                                                <p className="text-sm text-gray-600">{retriever.description}</p>
                                            </div>
                                        </CardContent>
                                    )}
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
